/**
 * BookDepthPanel - Full-width side-by-side orderbook depth for a selected game.
 *
 * Shows Kalshi + Polymarket ExchangeOrderBook for the selected side (away/home).
 * Inverts NO bids to YES asks for proper display.
 */

import { useState, useMemo } from 'react';
import { ExchangeOrderBook } from '@/components/organisms/ExchangeOrderBook';
import { mirrorCents } from '@/lib/pricing/subCent';
import type { BookLevel, Venue } from '@/types';
import type { GameRow, VenueBook } from '@/views/ValueDashboard/types';

// ---------------------------------------------------------------------------
// NO -> YES ask inversion
// ---------------------------------------------------------------------------

function invertNoToYesAsks(noBids: BookLevel[]): BookLevel[] {
  return noBids
    .map((l) => ({ priceCents: mirrorCents(l.priceCents), size: l.size }))
    .sort((a, b) => a.priceCents - b.priceCents);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface BookDepthPanelProps {
  game: GameRow;
  onClose: () => void;
}

export function BookDepthPanel({ game, onClose }: BookDepthPanelProps) {
  const [side, setSide] = useState<'away' | 'home'>('away');

  const teamLabel = side === 'away'
    ? (game.awayName || game.awayCode)
    : (game.homeName || game.homeCode);

  // Extract books for the selected side
  const kalshiBook: VenueBook | null = game.kalshi?.[side] ?? null;
  const polyBook: VenueBook | null = game.polymarket?.[side] ?? null;

  // Compute bids (YES bids) and asks (inverted NO bids) per venue
  const kalshiData = useMemo(() => {
    if (!kalshiBook) return null;
    return {
      bids: kalshiBook.yes,
      asks: invertNoToYesAsks(kalshiBook.no),
    };
  }, [kalshiBook]);

  const polyData = useMemo(() => {
    if (!polyBook) return null;
    return {
      bids: polyBook.yes,
      asks: invertNoToYesAsks(polyBook.no),
    };
  }, [polyBook]);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-border/50 flex h-8 shrink-0 items-center justify-between border-b px-3">
        <div className="flex items-center gap-3">
          <span className="text-foreground text-xs font-semibold">
            {game.awayCode} @ {game.homeCode}
          </span>
          <span className="text-muted-foreground text-[10px]">{teamLabel}</span>

          {/* Away / Home toggle */}
          <div className="bg-muted flex rounded text-[10px]">
            <button
              className={`rounded px-2 py-0.5 transition-colors ${
                side === 'away'
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setSide('away')}
            >
              {game.awayCode}
            </button>
            <button
              className={`rounded px-2 py-0.5 transition-colors ${
                side === 'home'
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setSide('home')}
            >
              {game.homeCode}
            </button>
          </div>
        </div>

        {/* Close button */}
        <button
          className="text-muted-foreground hover:text-foreground rounded p-1 transition-colors"
          onClick={onClose}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Side-by-side order books */}
      <div className="grid flex-1 grid-cols-2 gap-px overflow-hidden bg-border">
        {/* Kalshi */}
        <div className="overflow-y-auto bg-background p-2">
          {kalshiData ? (
            <ExchangeOrderBook
              bids={kalshiData.bids}
              asks={kalshiData.asks}
              venue={'kalshi' as Venue}
              teamLabel={teamLabel}
              maxLevels={12}
            />
          ) : (
            <div className="text-muted-foreground/50 flex h-full items-center justify-center text-xs">
              No orderbook data
            </div>
          )}
        </div>

        {/* Polymarket */}
        <div className="overflow-y-auto bg-background p-2">
          {polyData ? (
            <ExchangeOrderBook
              bids={polyData.bids}
              asks={polyData.asks}
              venue={'polymarket' as Venue}
              teamLabel={teamLabel}
              maxLevels={12}
            />
          ) : (
            <div className="text-muted-foreground/50 flex h-full items-center justify-center text-xs">
              No orderbook data
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
